import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  IconButton,
  Dialog,
  Alert,
  CircularProgress,
  Paper,
  Divider,
  Badge,
  Fab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Add,
  Assignment,
  QrCode,
  Print,
  Visibility,
  Schedule,
  PriorityHigh,
  CheckCircle,
  Warning,
  Settings,
  Refresh,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import samplesService, { Sample, Priority, SampleStatus } from '../../services/samplesService';
import { clientsService, Client } from '../../services/clientsService';
import CompleteSampleReceptionForm from '../forms/CompleteSampleReceptionForm';
import { usersService } from '../../services/usersService';
import { useOptimizedReceptionData } from '../../hooks/useOptimizedReceptionData';
import ReceptionSummaryPrint from '../reception/ReceptionSummaryPrint';
import ReactDOM from 'react-dom/client';

interface LabelConfig {
  width: number;
  height: number;
  name: string;
  dpi: number;
}

const LABEL_CONFIGS: LabelConfig[] = [
  { name: '62mm x 29mm (Brother DK-11209)', width: 732, height: 343, dpi: 300 },
  { name: '62mm x 100mm (Brother DK-11202)', width: 732, height: 1181, dpi: 300 },
  { name: '29mm x 90mm (Brother DK-11201)', width: 343, height: 1063, dpi: 300 },
  { name: '38mm x 90mm (Brother DK-11208)', width: 449, height: 1063, dpi: 300 },
];

const ReceptionistDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  
  // 🚀 PERFORMANCE: Use optimized data loading with safe caching
  const { 
    samples, 
    clients, 
    loading: dataLoading, 
    error: dataError, 
    stats, 
    refresh: refreshData 
  } = useOptimizedReceptionData();
  
  // Local UI state (unchanged)
  const [loading, setLoading] = useState(false); // For form operations
  const [error, setError] = useState<string | null>(null); // For form errors
  const [showReceptionForm, setShowReceptionForm] = useState(false);
  const [showLabelSettings, setShowLabelSettings] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [createdSample, setCreatedSample] = useState<any>(null);
  const [labelConfig, setLabelConfig] = useState<LabelConfig>(LABEL_CONFIGS[0]); // Default to 62x29mm
  // 🚀 PERFORMANCE: Data loading is now handled by the optimized hook
  // No need for manual loadReceptionData function

  const handleSampleCreated = async (formData: any) => {
    try {
      setLoading(true);
      setError('');
      
      
      
      // Ensure analyses_demandees is included in the sample data
      const sampleData = {
        ...formData,
        // Ensure analyses_demandees is in both top level and metadata
        analyses_demandees: formData.analyses_demandees || [],
        metadonnees: {
          ...formData.metadonnees,
          analyses_demandees: formData.analyses_demandees || [],
          selected_elements: formData.selected_elements || [],
          // Include other form fields in metadata for workflow routing
          poids_kg: formData.poids_kg,
          nombre_sacs: formData.nombre_sacs,
          etat_echantillon: formData.etat_echantillon,
          humidite_echantillon: formData.humidite_echantillon,
          echantillonnage_par: formData.echantillonnage_par,
          priorite_analyse: formData.priorite_analyse,
        }
      };
      

      
      // Validate that methods are selected
      if (!sampleData.analyses_demandees || sampleData.analyses_demandees.length === 0) {
        throw new Error('Aucune méthode d\'analyse sélectionnée. Veuillez sélectionner au moins une méthode.');
      }
      
      const createdSample = await samplesService.createSample(sampleData);
      
      
      setShowReceptionForm(false);
      setCreatedSample(createdSample);
      
      // Set beautiful success message with workflow information
      const mechanicalPrepInfo = sampleData.mechanical_preparation_methods?.length > 0 
        ? `\n🔧 Préparation mécanique: ${sampleData.mechanical_preparation_methods.length} méthode(s) sélectionnée(s)`
        : '';
      
      setSuccessMessage(
        `Échantillon ${createdSample.sample_id} créé avec succès!` +
        `\n📊 ${sampleData.analyses_demandees.length} méthode(s) d'analyse sélectionnée(s)` +
        mechanicalPrepInfo +
        `\n📍 Statut: ${createdSample.statut}` +
        `\n🏭 Prochaine étape: ${(createdSample as any).current_laboratory || 'Routage automatique'}`
      );
      
      await refreshData(); // 🚀 PERFORMANCE: Use optimized refresh
      
    } catch (error) {
      console.error('❌ Error creating sample:', error);
      setError((error as Error).message || 'Erreur lors de la création de l\'échantillon');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateQR = async (sampleId: string) => {
    try {
      console.log('🔄 Generating QR code for sample ID:', sampleId);
      
      // Get the sample details first to get the sample_id
      console.log('📋 Fetching sample details...');
      const sample = await samplesService.getSample(sampleId);
      console.log('✅ Sample details fetched:', sample.sample_id);
      
      // Get QR code image blob from backend (simple = just sample_id for easy scanning)
      console.log('🔄 Generating QR code blob...');
      const qrBlob = await samplesService.generateQRCode(sampleId, 'simple');
      console.log('✅ QR code blob generated, size:', qrBlob.size, 'bytes');
      
      // Create a formatted label for printing
      console.log('🖨️ Creating print label...');
      await printSampleLabel(sample, qrBlob);
      console.log('✅ Print label created successfully!');
      
    } catch (error) {
      console.error('❌ Error generating QR code:', error);
      
      // Provide detailed error information
      if (error.message.includes('Failed to fetch')) {
        throw new Error('Connexion au serveur impossible. Vérifiez que le backend est démarré.');
      } else if (error.message.includes('404')) {
        throw new Error('Échantillon non trouvé sur le serveur.');
      } else if (error.message.includes('500')) {
        throw new Error('Erreur serveur lors de la génération du QR code.');
      } else {
        throw new Error(`Erreur QR code: ${error.message}`);
      }
    }
  };

  const printSampleLabel = async (sample: any, qrBlob: Blob) => {
    try {
      // Create a canvas for the label
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Label dimensions (adjust for your label printer - example: 62mm x 29mm at 300 DPI)
      const labelWidth = labelConfig.width;
      const labelHeight = labelConfig.height;
      
      canvas.width = labelWidth;
      canvas.height = labelHeight;
      
      if (!ctx) throw new Error('Could not get canvas context');
      
      // Professional gradient background
      const gradient = ctx.createLinearGradient(0, 0, 0, labelHeight);
      gradient.addColorStop(0, '#ffffff');
      gradient.addColorStop(1, '#f8f9fa');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, labelWidth, labelHeight);
      
      // Add subtle border
      ctx.strokeStyle = '#e9ecef';
      ctx.lineWidth = 2;
      ctx.strokeRect(1, 1, labelWidth - 2, labelHeight - 2);
      
      // Create QR code image
      const qrUrl = URL.createObjectURL(qrBlob);
      const qrImage = new Image();
      
      qrImage.onload = () => {
        // Adaptive layout based on label dimensions
        const isWideLabel = labelWidth > labelHeight;
        const margin = 10;
        
        if (isWideLabel) {
          // Wide label: QR code on left, text on right
          const qrSize = Math.min(labelHeight - (margin * 3), labelWidth * 0.35);
          const qrX = margin * 1.5;
          const qrY = (labelHeight - qrSize) / 2;
          
          // QR code with subtle shadow
          ctx.shadowColor = 'rgba(0, 0, 0, 0.1)';
          ctx.shadowBlur = 4;
          ctx.shadowOffsetX = 2;
          ctx.shadowOffsetY = 2;
          ctx.drawImage(qrImage, qrX, qrY, qrSize, qrSize);
          ctx.shadowColor = 'transparent';
          
          // Text area on the right
          const textX = qrX + qrSize + margin * 1.5;
          const textWidth = labelWidth - textX - margin;
          
          // Adaptive font sizes based on available space
          const baseFontSize = Math.min(labelHeight / 6, 32);
          
          // AFRILAB header (smaller and positioned better)
          ctx.fillStyle = '#1976d2';
          ctx.font = `bold ${baseFontSize * 0.5}px 'Segoe UI', Arial, sans-serif`;
          ctx.textAlign = 'left';
          ctx.textBaseline = 'top';
          const headerY = margin;
          ctx.fillText('AFRILAB MINING', textX, headerY);
          
          // Main sample ID - MUCH LARGER and more prominent
          ctx.fillStyle = '#212529';
          ctx.font = `bold ${baseFontSize * 1.8}px 'Segoe UI', Arial, sans-serif`;
          ctx.textBaseline = 'middle';
          
          const mainY = labelHeight / 2 - baseFontSize * 0.2;
          ctx.fillText(sample.sample_id, textX, mainY);
          
          // Underline for sample ID (thicker)
          ctx.strokeStyle = '#1976d2';
          ctx.lineWidth = 3;
          ctx.beginPath();
          ctx.moveTo(textX, mainY + baseFontSize * 0.6);
          ctx.lineTo(textX + ctx.measureText(sample.sample_id).width, mainY + baseFontSize * 0.6);
          ctx.stroke();
          
          // Client name with proper icon replacement
          ctx.fillStyle = '#495057';
          ctx.font = `${baseFontSize * 0.5}px 'Segoe UI', Arial, sans-serif`;
          const clientY = mainY + baseFontSize * 1.4;
          const clientText = sample.client?.company_name || 'Client';
          const maxClientLength = Math.floor(textWidth / (baseFontSize * 0.3));
          
          // Draw building icon manually (simple rectangle)
          ctx.fillStyle = '#495057';
          ctx.fillRect(textX, clientY - baseFontSize * 0.2, baseFontSize * 0.3, baseFontSize * 0.25);
          ctx.strokeStyle = '#495057';
          ctx.lineWidth = 1;
          ctx.strokeRect(textX, clientY - baseFontSize * 0.2, baseFontSize * 0.3, baseFontSize * 0.25);
          
          // Client text
          ctx.fillText(clientText.substring(0, maxClientLength), textX + baseFontSize * 0.4, clientY);
          
          // Date with calendar icon replacement
          ctx.fillStyle = '#6c757d';
          ctx.font = `${baseFontSize * 0.4}px 'Segoe UI', Arial, sans-serif`;
          const dateY = clientY + baseFontSize * 0.7;
          const date = new Date(sample.date_creation).toLocaleDateString('fr-FR');
          
          // Draw calendar icon manually (simple square with lines)
          ctx.fillStyle = '#6c757d';
          ctx.strokeStyle = '#6c757d';
          ctx.lineWidth = 1;
          ctx.strokeRect(textX, dateY - baseFontSize * 0.15, baseFontSize * 0.25, baseFontSize * 0.2);
          ctx.beginPath();
          ctx.moveTo(textX + baseFontSize * 0.05, dateY - baseFontSize * 0.05);
          ctx.lineTo(textX + baseFontSize * 0.2, dateY - baseFontSize * 0.05);
          ctx.stroke();
          
          // Date text
          ctx.fillText(date, textX + baseFontSize * 0.35, dateY);
          
        } else {
          // Tall label: QR code on top, text below
          const qrSize = Math.min(labelWidth - (margin * 3), labelHeight * 0.55);
          const qrX = (labelWidth - qrSize) / 2;
          const qrY = margin * 1.5;
          
          // QR code with subtle shadow
          ctx.shadowColor = 'rgba(0, 0, 0, 0.1)';
          ctx.shadowBlur = 4;
          ctx.shadowOffsetX = 2;
          ctx.shadowOffsetY = 2;
          ctx.drawImage(qrImage, qrX, qrY, qrSize, qrSize);
          ctx.shadowColor = 'transparent';
          
          // Text area below QR code
          const textY = qrY + qrSize + margin;
          const textHeight = labelHeight - textY - margin;
          
          // Adaptive font sizes
          const baseFontSize = Math.min(labelWidth / 8, textHeight / 4, 28);
          
          // AFRILAB header (centered, smaller)
          ctx.fillStyle = '#1976d2';
          ctx.font = `bold ${baseFontSize * 0.5}px 'Segoe UI', Arial, sans-serif`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'top';
          const headerY = textY;
          ctx.fillText('AFRILAB MINING', labelWidth / 2, headerY);
          
          // Main sample ID (centered) - MUCH LARGER
          const mainY = headerY + baseFontSize * 0.6;
          ctx.fillStyle = '#212529';
          ctx.font = `bold ${baseFontSize * 1.8}px 'Segoe UI', Arial, sans-serif`;
          const sampleIdWidth = ctx.measureText(sample.sample_id).width;
          
          // Background rectangle for sample ID (larger)
          ctx.fillStyle = '#f8f9fa';
          ctx.strokeStyle = '#1976d2';
          ctx.lineWidth = 2;
          const rectPadding = 12;
          const rectX = (labelWidth - sampleIdWidth - rectPadding * 2) / 2;
          const rectY = mainY - rectPadding;
          const rectWidth = sampleIdWidth + rectPadding * 2;
          const rectHeight = baseFontSize * 1.4 + rectPadding;
          ctx.fillRect(rectX, rectY, rectWidth, rectHeight);
          ctx.strokeRect(rectX, rectY, rectWidth, rectHeight);
          
          // Sample ID text
          ctx.fillStyle = '#212529';
          ctx.textBaseline = 'top';
          ctx.fillText(sample.sample_id, labelWidth / 2, mainY);
          
          // Client name (smaller but still visible)
          const clientY = mainY + baseFontSize * 1.2;
          ctx.fillStyle = '#495057';
          ctx.font = `${baseFontSize * 0.7}px 'Segoe UI', Arial, sans-serif`;
          const clientName = sample.client?.company_name || 'Client';
          const maxClientWidth = labelWidth * 0.9;
          const clientText = clientName.length > 20 ? clientName.substring(0, 20) + '...' : clientName;
          ctx.fillText(clientText, labelWidth / 2, clientY);
          
          // Batch info (even smaller)
          const batchY = clientY + baseFontSize * 0.8;
          ctx.fillStyle = '#6c757d';
          ctx.font = `${baseFontSize * 0.6}px 'Segoe UI', Arial, sans-serif`;
          const batchText = sample.batch_number ? `Lot: ${sample.batch_number}` : 'Lot: N/A';
          ctx.fillText(batchText, labelWidth / 2, batchY);
          
          // Date (smallest)
          const dateY = batchY + baseFontSize * 0.7;
          ctx.font = `${baseFontSize * 0.5}px 'Segoe UI', Arial, sans-serif`;
          const dateStr = new Date().toLocaleDateString('fr-FR');
          ctx.fillText(dateStr, labelWidth / 2, dateY);
        }
        
        // Convert canvas to blob and print directly
        canvas.toBlob((blob) => {
          if (blob) {
            // Create a hidden iframe for printing
            const iframe = document.createElement('iframe');
            iframe.style.position = 'absolute';
            iframe.style.top = '-9999px';
            iframe.style.left = '-9999px';
            iframe.style.width = '1px';
            iframe.style.height = '1px';
            document.body.appendChild(iframe);
            
            const url = URL.createObjectURL(blob);
            
            iframe.onload = () => {
              const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
              if (iframeDoc) {
                iframeDoc.write(`
                  <!DOCTYPE html>
                  <html>
                  <head>
                    <title>Label - ${sample.sample_id}</title>
                    <style>
                      @page {
                        size: ${Math.round(labelConfig.width / labelConfig.dpi * 25.4)}mm ${Math.round(labelConfig.height / labelConfig.dpi * 25.4)}mm;
                        margin: 0;
                      }
                      body {
                        margin: 0;
                        padding: 0;
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        width: 100%;
                        height: 100vh;
                      }
                      img {
                        width: 100%;
                        height: auto;
                        object-fit: contain;
                      }
                    </style>
                  </head>
                  <body>
                    <img src="${url}" alt="Sample Label ${sample.sample_id}" onload="window.print();" />
                  </body>
                  </html>
                `);
                iframeDoc.close();
                
                // Clean up after printing
                setTimeout(() => {
                  URL.revokeObjectURL(url);
                  document.body.removeChild(iframe);
                }, 3000);
              }
            };
            
            iframe.src = 'about:blank';
          }
        }, 'image/png');
        
        URL.revokeObjectURL(qrUrl);
      };
      
      qrImage.src = qrUrl;
      
    } catch (error) {
      console.error('Error creating label:', error);
      setError('Erreur lors de la création de l\'étiquette');
    }
  };

  const handlePrintLabel = async (sampleId: string) => {
    try {
      // For now, just show a message since backend doesn't have label endpoint
      alert(`Impression d'étiquette pour l'échantillon ${sampleId} - Fonctionnalité à implémenter`);
    } catch (error) {
      console.error('Error printing label:', error);
      setError('Erreur lors de l\'impression de l\'étiquette');
    }
  };

  const handlePrintReceptionSummary = (sample: Sample) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Veuillez autoriser les popups pour imprimer');
      return;
    }

    // Create a container for the React component
    const container = document.createElement('div');
    printWindow.document.body.appendChild(container);

    // Prepare the data for individual sample
    const batchData = {
      batchNumber: sample.sample_id,
      client: sample.client,
      samples: [sample], // Single sample in array
      selectedElements: sample.metadonnees?.selected_elements || [],
      analysesDemandees: sample.metadonnees?.analyses_demandees || [],
      mechanicalPreparationMethods: sample.metadonnees?.mechanical_preparation_methods || [],
      priorite: sample.priorite,
      dateReception: new Date(sample.date_reception),
      recuPar: user?.firstName + ' ' + user?.lastName || 'Réceptionniste'
    };

    // Render the component
    const root = ReactDOM.createRoot(container);
    root.render(<ReceptionSummaryPrint batchData={batchData} />);

    // Add print styles and trigger print after a short delay
    setTimeout(() => {
      printWindow.document.head.innerHTML = `
        <title>Résumé Réception - ${sample.sample_id}</title>
        <style>
          @media print {
            body { margin: 0; }
            @page { margin: 0; }
          }
          body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 0;
          }
        </style>
      `;
      
      // Import MUI styles
      const muiStyles = document.querySelectorAll('style[data-emotion], style[data-mui]');
      muiStyles.forEach(style => {
        printWindow.document.head.appendChild(style.cloneNode(true));
      });

      printWindow.print();
      
      // Close after printing
      setTimeout(() => {
        printWindow.close();
      }, 1000);
    }, 500);
  };

  const getPriorityColor = (priority: Priority) => {
    switch (priority) {
      case Priority.URGENTE:
        return 'error';
      case Priority.ELEVEE:
        return 'warning';
      case Priority.NORMALE:
        return 'default';
      default:
        return 'default';
    }
  };

  const getPriorityIcon = (priority: Priority) => {
    switch (priority) {
      case Priority.URGENTE:
        return <PriorityHigh />;
      case Priority.ELEVEE:
        return <Warning />;
      default:
        return <Schedule />;
    }
  };

  // Show loading spinner for initial data load
  if (dataLoading && !samples.length) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Chargement des données de réception...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Welcome Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Bonjour, {user?.firstName} 👋
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Tableau de bord - Réception des échantillons
          </Typography>
        </Box>
        <IconButton
          onClick={() => refreshData()}
          disabled={dataLoading}
          title="Actualiser les données"
          sx={{ 
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.2) }
          }}
        >
          <Refresh />
        </IconButton>
      </Box>

      {/* Data loading error */}
      {dataError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {dataError}
        </Alert>
      )}

      {/* Form operation error */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Beautiful Success Notification */}
      <Dialog
        open={!!successMessage}
        onClose={() => {
          setSuccessMessage(null);
          setCreatedSample(null);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            background: 'linear-gradient(135deg, #e8f5e8 0%, #f0f9ff 100%)',
            border: '2px solid #4caf50',
          }
        }}
      >
        <DialogTitle sx={{ 
          textAlign: 'center', 
          pb: 1,
          background: 'linear-gradient(135deg, #4caf50, #66bb6a)',
          color: 'white',
          borderRadius: '16px 16px 0 0',
          mb: 2
        }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <CheckCircle sx={{ fontSize: 64, animation: 'bounce 0.6s ease-in-out' }} />
            <Typography variant="h5" fontWeight="bold">
              ✅ Échantillon Créé avec Succès!
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ px: 4, py: 3 }}>
          {createdSample && (
            <Box>
              {/* Sample Information Card */}
              <Card sx={{ 
                mb: 3, 
                background: 'rgba(255, 255, 255, 0.9)',
                border: '1px solid #e0e0e0',
                borderRadius: 3
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Assignment sx={{ color: '#1976d2', mr: 1, fontSize: 28 }} />
                    <Typography variant="h6" fontWeight="bold" color="primary">
                      {createdSample.sample_id}
                    </Typography>
                  </Box>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Client:</Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {createdSample.client?.company_name || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Type:</Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {createdSample.type_echantillon}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Statut:</Typography>
                      <Chip 
                        label={createdSample.statut} 
                        size="small" 
                        color="primary"
                        sx={{ fontWeight: 'bold' }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Priorité:</Typography>
                      <Chip 
                        label={createdSample.priorite} 
                        size="small" 
                        color={getPriorityColor(createdSample.priorite)}
                        sx={{ fontWeight: 'bold' }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Workflow Information */}
              <Alert severity="info" sx={{ mb: 3, borderRadius: 3 }}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  <strong>🔄 Prochaine Étape:</strong> L'échantillon a été automatiquement routé vers{' '}
                  <strong>{(createdSample as any).current_laboratory || createdSample.statut}</strong>
                </Typography>
                {successMessage && (
                  <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-line' }}>
                    {successMessage.split('\n').slice(1).join('\n')}
                  </Typography>
                )}
              </Alert>

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                <Button
                  variant="outlined"
                  startIcon={<QrCode />}
                  onClick={() => handleGenerateQR(createdSample.id)}
                  sx={{ borderRadius: 3 }}
                >
                  Générer QR Code
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Print />}
                  onClick={() => handlePrintLabel(createdSample.id)}
                  sx={{ borderRadius: 3 }}
                >
                  Imprimer Étiquette
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 3, justifyContent: 'center', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => {
              if (createdSample) {
                handlePrintReceptionSummary(createdSample);
              }
            }}
            startIcon={<Print />}
            sx={{ borderRadius: 3 }}
          >
            Imprimer Résumé
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setSuccessMessage(null);
              setCreatedSample(null);
            }}
            sx={{ 
              px: 4, 
              py: 1.5, 
              borderRadius: 3,
              background: 'linear-gradient(135deg, #4caf50, #66bb6a)',
              '&:hover': {
                background: 'linear-gradient(135deg, #45a049, #5db85d)',
              }
            }}
          >
            Parfait! 🎉
          </Button>
        </DialogActions>
      </Dialog>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.2)} 100%)`,
            borderLeft: `6px solid ${theme.palette.primary.main}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            overflow: 'hidden',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 16px 30px ${alpha(theme.palette.primary.main, 0.3)}`,
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '100px',
              height: '100px',
              background: `linear-gradient(45deg, ${alpha(theme.palette.primary.main, 0.1)}, transparent)`,
              borderRadius: '50%',
              transform: 'translate(50%, -50%)',
            }
          }}>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Box sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.8rem',
                  boxShadow: `0 8px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
                  transition: 'transform 0.2s ease',
                  '&:hover': { transform: 'scale(1.1)' },
                  mr: 2
                }}>
                  📥
                </Box>
                <Box>
                  <Typography variant="h4" component="div" sx={{
                    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontWeight: 'bold'
                  }}>
                    {stats.today_received}
                  </Typography>
                  <Typography color="text.secondary" sx={{ fontWeight: 500 }}>
                    Reçus aujourd'hui
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card sx={{
            background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.1)} 0%, ${alpha(theme.palette.warning.main, 0.2)} 100%)`,
            borderLeft: `6px solid ${theme.palette.warning.main}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            overflow: 'hidden',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 16px 30px ${alpha(theme.palette.warning.main, 0.3)}`,
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '100px',
              height: '100px',
              background: `linear-gradient(45deg, ${alpha(theme.palette.warning.main, 0.1)}, transparent)`,
              borderRadius: '50%',
              transform: 'translate(50%, -50%)',
            }
          }}>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Box sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.8rem',
                  boxShadow: `0 8px 16px ${alpha(theme.palette.warning.main, 0.3)}`,
                  transition: 'transform 0.2s ease',
                  '&:hover': { transform: 'scale(1.1)' },
                  mr: 2
                }}>
                  ⏳
                </Box>
                <Box>
                  <Typography variant="h4" component="div" sx={{
                    background: `linear-gradient(45deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontWeight: 'bold'
                  }}>
                    {stats.pending_reception}
                  </Typography>
                  <Typography color="text.secondary" sx={{ fontWeight: 500 }}>
                    En attente réception
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card sx={{
            background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.1)} 0%, ${alpha(theme.palette.error.main, 0.2)} 100%)`,
            borderLeft: `6px solid ${theme.palette.error.main}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            overflow: 'hidden',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 16px 30px ${alpha(theme.palette.error.main, 0.3)}`,
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '100px',
              height: '100px',
              background: `linear-gradient(45deg, ${alpha(theme.palette.error.main, 0.1)}, transparent)`,
              borderRadius: '50%',
              transform: 'translate(50%, -50%)',
            }
          }}>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Box sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.8rem',
                  boxShadow: `0 8px 16px ${alpha(theme.palette.error.main, 0.3)}`,
                  transition: 'transform 0.2s ease',
                  '&:hover': { transform: 'scale(1.1)' },
                  mr: 2
                }}>
                  🚨
                </Box>
                <Box>
                  <Typography variant="h4" component="div" sx={{
                    background: `linear-gradient(45deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontWeight: 'bold'
                  }}>
                    {stats.urgent_samples}
                  </Typography>
                  <Typography color="text.secondary" sx={{ fontWeight: 500 }}>
                    Échantillons urgents
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Action Button */}
      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <Button
          variant="contained"
          size="large"
          startIcon={<Add />}
          onClick={() => setShowReceptionForm(true)}
          sx={{ 
            py: 2, 
            px: 4, 
            fontSize: '1.1rem',
            borderRadius: 2,
            mr: 2,
          }}
        >
          Nouvel Échantillon
        </Button>
        
        <Button
          variant="outlined"
          size="large"
          startIcon={<Settings />}
          onClick={() => setShowLabelSettings(!showLabelSettings)}
          sx={{ 
            py: 2, 
            px: 3, 
            borderRadius: 2,
          }}
        >
          Config. Étiquettes
        </Button>
      </Box>

      {/* Label Configuration */}
      <Collapse in={showLabelSettings}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Configuration des Étiquettes
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Format d'étiquette</InputLabel>
                <Select
                  value={LABEL_CONFIGS.findIndex(config => config.name === labelConfig.name)}
                  onChange={(e) => setLabelConfig(LABEL_CONFIGS[e.target.value as number])}
                  label="Format d'étiquette"
                >
                  {LABEL_CONFIGS.map((config, index) => (
                    <MenuItem key={index} value={index}>
                      {config.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                Dimensions: {labelConfig.width}x{labelConfig.height}px ({labelConfig.dpi} DPI)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Taille physique: {Math.round(labelConfig.width / labelConfig.dpi * 25.4)}mm x {Math.round(labelConfig.height / labelConfig.dpi * 25.4)}mm
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      </Collapse>

      {/* Recent Samples List */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Échantillons Récents
        </Typography>
        
        {samples.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Typography color="text.secondary">
              Aucun échantillon en attente de réception
            </Typography>
          </Box>
        ) : (
          <List>
            {samples.map((sample, index) => (
              <React.Fragment key={sample.id}>
                <ListItem
                  sx={{ 
                    px: 2,
                    '&:hover': { backgroundColor: 'action.hover' }
                  }}
                >
                  <ListItemIcon>
                    <Badge
                      color={getPriorityColor(sample.priorite as Priority)}
                      variant="dot"
                      invisible={sample.priorite === Priority.NORMALE}
                    >
                      <Assignment />
                    </Badge>
                  </ListItemIcon>
                  
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="subtitle1" fontWeight="medium">
                          {sample.sample_id}
                        </Typography>
                        <Chip
                          icon={getPriorityIcon(sample.priorite as Priority)}
                          label={sample.priorite}
                          size="small"
                          color={getPriorityColor(sample.priorite as Priority)}
                        />
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Client: {sample.client?.company_name || 'N/A'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Reçu: {sample.date_reception ? new Date(sample.date_reception).toLocaleDateString('fr-FR') : 'N/A'}
                        </Typography>
                      </Box>
                    }
                  />
                  
                  <Box display="flex" gap={1}>
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/samples/${sample.id}`)}
                      title="Voir détails"
                    >
                      <Visibility />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleGenerateQR(sample.id)}
                      title="Générer QR Code"
                    >
                      <QrCode />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handlePrintLabel(sample.id)}
                      title="Imprimer Étiquette"
                    >
                      <Print />
                    </IconButton>
                  </Box>
                </ListItem>
                {index < samples.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>

      {/* Quick Actions FAB */}
      <Fab
        color="primary"
        aria-label="scan qr"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={() => navigate('/qr-scanner')}
      >
        <QrCode />
      </Fab>

      {/* Reception Form Dialog */}
      <Dialog
        open={showReceptionForm}
        onClose={() => setShowReceptionForm(false)}
        maxWidth="lg"
        fullWidth
      >
        <CompleteSampleReceptionForm
          onSubmit={handleSampleCreated}
          onCancel={() => setShowReceptionForm(false)}
        />
      </Dialog>
    </Box>
  );
};

export default ReceptionistDashboard; 